import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useUser } from '@/modules/common/application/context/user';
import { createFirebaseUser, loginWithSAML, logoutUser } from '@/services/auth.service';
import { ACTIVATE_USER, RESEND_VERIFY_EMAIL } from '@/services/graphql/mutations';
import { sendMessageToSlack } from '@/services/slack.service';

import { useMessage } from '../../common/application/context/message';
import useEnhancedMutation from '../../common/application/use-enhanced-mutation';
import {
  ActivateUserMutation,
  ActivateUserMutationVariables,
  ResendVerifyEmailMutation,
  ResendVerifyEmailMutationVariables,
} from '../../common/types';
import { useTenantInfo } from './use-tenant-info';

const useRegister = () => {
  const { t } = useTranslation();
  const [showVerifyEmail, setShowVerifyEmail] = useState(false);
  const [email, setEmail] = useState('');
  const { setErrorMessage, setSuccessMessage } = useMessage();
  const navigate = useNavigate();
  const { refetch, setIsRegistering, isRegistering } = useUser();
  const { refetch: fetchTenantInfo, loading: isFetchingTenantInfo } = useTenantInfo();
  const [confirmRegistration, setConfirmRegistration] = useState(false);

  const [activateUser] = useEnhancedMutation<ActivateUserMutation, ActivateUserMutationVariables>(
    ACTIVATE_USER
  );

  const [resendVerifyEmail] = useEnhancedMutation<
    ResendVerifyEmailMutation,
    ResendVerifyEmailMutationVariables
  >(RESEND_VERIFY_EMAIL, {
    onError: () => setErrorMessage(t('messages.resendVerifyEmail.error')),
    onCompleted: () => setSuccessMessage(t('messages.resendVerifyEmail.success')),
    fetchPolicy: 'no-cache',
  });

  const handleRegistrationError = async (error: any, email: string) => {
    if (error.message === 'User does not exist') {
      const currentRoute = window.location.pathname;
      setConfirmRegistration(true);
      await sendMessageToSlack(`Leeeaaaad *_*: ${email} ${currentRoute}`);
    } else if (error.message === 'User is already registered') {
      setErrorMessage(t('messages.userAlreadyRegistered.error'));
    } else {
      console.error(error);
      setErrorMessage(t('messages.register.error'));
    }
  };

  const registerUser = async (email: string) => {
    setEmail(email);
    setIsRegistering(true);
    try {
      const { data } = await fetchTenantInfo({ input: { email, isSignUp: true } });
      const tenantInfo = data?.tenantInfo;
      if (tenantInfo?.providerId) {
        await loginWithSAML(tenantInfo);
        await activateUser({
          variables: { input: { email, isSaml: true } },
        });
        setIsRegistering(false);
        await refetch();
      } else {
        try {
          await createFirebaseUser(email, tenantInfo?.tenantId || null);
        } catch (error: any) {
          if (error.code !== 'auth/email-already-in-use') {
            throw error;
          }
        }
        await logoutUser(tenantInfo?.tenantId || null);
        await activateUser({
          variables: { input: { email, isSaml: false } },
        });
        setIsRegistering(false);
        setShowVerifyEmail(true);
        navigate(`/resend-verification?email=${encodeURIComponent(email)}`);
      }
    } catch (error: any) {
      await handleRegistrationError(error, email);
      setIsRegistering(false);
    }
  };

  return {
    registerUser,
    resendVerifyEmail,
    showVerifyEmail,
    email,
    registerUserLoading: isRegistering || isFetchingTenantInfo,
    confirmRegistration,
  };
};

export default useRegister;
